import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { StorageKeys } from '@enums/storage-keys';
import { environment } from '@environments/environment';
import { HealthLabsLoadCartOptions } from '@interfaces/healthlabs-load-cart-options';
import { InHomeCollectionService, Result, ResultService } from '@Medology/ng-findalab';
import { Address } from '@models/address';
import { AnalyteCareCouponDto } from '@models/analyte-care-coupon-dto';
import { AnalyteCareLoadCartResponse } from '@models/analyte-care-load-cart-response';
import { Coupon } from '@models/coupon';
import { Fee } from '@models/fee';
import { HealthLabsLoadCartRequest } from '@models/healthlabs-load-cart-request';
import { HealthLabsLoadCartResponse } from '@models/healthlabs-load-cart-response';
import { OrderRequest } from '@models/order-request';
import { Reorder } from '@models/reorder';
import { Test } from '@models/test';
import { AuthorizationService } from '@services/authorization.service';
import { ConsultationStorageService } from '@services/consultation-storage.service';
import { DataLayerService } from '@services/data-layer.service';
import { ErrorLoggingService } from '@services/error-logging.service';
import { FormService } from '@services/form.service';
import { GraphqlService } from '@services/graphql.service';
import { InHomeBookingReloadOrderService } from '@services/in-home-booking-reload-order.service';
import { StorageService } from '@services/storage.service';
import { TreatmentTypeService } from '@services/treatment-type.service';
import { catchError, Observable, of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class HealthLabsOrderService extends InHomeBookingReloadOrderService {
  public findTestsModalState = false;

  protected document = inject<Document>(DOCUMENT);
  protected http = inject(HttpClient);
  protected router = inject(Router);
  protected storage = inject(StorageService);
  protected results = inject(ResultService);
  protected formService = inject(FormService);
  protected authService = inject(AuthorizationService);
  protected inHomeService = inject(InHomeCollectionService);
  protected dataLayerService = inject(DataLayerService);
  protected consultationStorageService = inject(ConsultationStorageService);
  protected errorLoggingService = inject(ErrorLoggingService);
  protected treatmentTypeService = inject(TreatmentTypeService);
  protected graphqlService = inject(GraphqlService);

  /**
   * Get tests from url.
   */
  public override get testsFromUrl(): string[] {
    if (this.storage.baOrder?.isActive) {
      return super.testsFromUrl;
    }

    return [];
  }

  /**
   * Load Healthlabs categories.
   */
  public getTestCategories(): Observable<any> {
    return this.http.get(`${environment.siteUrls.tests}/categories?levels=1`);
  }

  /**
   * Remove test from cart.
   *
   * @param {number} entryId The test entry id
   */
  public removeTestFromCart(entryId: number): Observable<any> {
    let body = new FormData();
    body.append('entry_id', entryId.toString());

    return this.http.post<any>(`${environment.apiUrl}/remove`, body);
  }

  /**
   * Clear last order from storage and Craft session.
   */
  public override clearLastOrder(): void {
    super.clearLastOrder();
    this.clearCartFromSession().subscribe();
  }

  /**
   * Parses HealthLabs a load cart JSON response to match the project structure.
   *
   * @param {HealthLabsLoadCartResponse} response the API response to parse
   *
   * @returns {Observable<AnalyteCareLoadCartResponse>} an Observable of the AnalyteCareLoadCartResponse
   */
  public override parseCartResponse(response: HealthLabsLoadCartResponse): Observable<AnalyteCareLoadCartResponse> {
    const tests = this.mapHlCartResponseTests(response);

    return of({
      fee: response.compliance_fee ? new Fee(response.compliance_fee.name, +response.compliance_fee.value * 100) : null,
      center: response.lab,
      tests: tests,
      discount: response.discount ? response.discount : null,
      coupon: response.coupon ? new Coupon(response.coupon, this.getCartSubtotal(tests)) : null,
      total: 0,
    });
  }

  /**
   * Load HealthLabs cart.
   *
   * @param {Test[]}                    tests   List of tests to be loaded in the cart. If not provided,
   *                                            the tests will be loaded from the URL.
   * @param {HealthLabsLoadCartOptions} options additional data that will be used by specific load cart implementation.
   *
   * @returns {Observable<AnalyteCareLoadCartResponse>} an Observable of the AnalyteCare load cart response.
   **/
  public override loadCart(
    tests?: Test[],
    options?: HealthLabsLoadCartOptions,
  ): Observable<AnalyteCareLoadCartResponse> {
    const request = new HealthLabsLoadCartRequest({
      labId: options?.labId,
      tests: this.getLoadCartTests(tests),
      accountToken: this.storage.baOrder?.isActive ? this.storage.authToken : null,
      coupon: this.couponCode,
      hasInHomeCollection: options ? options.hasInHomeCollection : this.storage.hasInHomeCollection,
    });
    this.storage.remove(StorageKeys.Center);

    return this.loadCartRequest(request);
  }

  /**
   * Get a Subscription to the lab selection that chooses the tests in each event.
   *
   * @returns a Subscription to resultSelected$ Observable
   */
  public override listenForLabSelection(): Subscription {
    return this.results.resultSelected$.subscribe((result: Result) => {
      this.loadCart([], { labId: result.id }).subscribe();
    });
  }

  /**
   * Update Current Order lab on Healthlabs Craft Session and on Order.
   *
   * @param {number} centerId The lab id to update
   * @param {string} hash The order hash to update the lab for
   */
  public override updateLab(centerId: number, hash: string): Observable<{ success: Boolean }> {
    return this.http.post(`${environment.apiUrl}/set-lab`, { labId: centerId }).pipe(
      catchError((error) => {
        this.errorLoggingService.logError(error);

        return of(null);
      }),
      switchMap(() => super.updateLab(centerId, hash)),
    );
  }

  /**
   * Creates a HealthLabsLoadCartRequest from a Reorder object.
   *
   * @param {Reorder} reorder the Reorder object containing the details of the order to reorder
   */
  public override getOrderRequestFromReorder(reorder: Reorder): HealthLabsLoadCartRequest {
    return new HealthLabsLoadCartRequest({
      labId: reorder.center?.id,
      tests: reorder.tests.map((customerTest) => customerTest.test.id),
      coupon: reorder.coupon_code,
    });
  }

  /**
   * Gets the total coupon discount amount in cents.
   */
  public override getTotalCoupons(): number {
    if (!this.storage.coupon) {
      return 0;
    }

    return this.storage.coupon.value * 100;
  }

  /**
   * Update discounts in local storage.
   *
   * @param {AnalyteCareLoadCartResponse} response the load-cart response from HealthLabs
   */
  protected override saveCartResponse(response: AnalyteCareLoadCartResponse): void {
    super.saveCartResponse(response);

    if (this.storage.baOrder?.isActive) {
      this.storage.remove(StorageKeys.Coupon);
      this.storage.remove(StorageKeys.Discount);

      if (this.storage.baOrder?.isStateComplianceFeeWaived) {
        this.storage.remove(StorageKeys.Fee);
      }

      return;
    }

    this.storage.discount = response.discount;
  }

  /**
   * Responds with the coupon code as an observable.
   *
   * TODO: https://github.com/Medology/ng-checkout/issues/1269
   * This method does nothing currently.
   *
   * @param {string} code the coupon code
   */
  protected override getCoupon(code: string): Observable<Coupon> {
    return of({ coupon_code: code } as Coupon);
  }

  /**
   * Calculates the total amount of the discount by adding the amount of discount for
   * buying multiple tests plus the partner testing discount if applicable.
   *
   * @returns the amount of the discount
   */
  protected override getTotalDiscount(): number {
    return (
      (this.storage.discount ? Number(this.storage.discount.value) * 100 : 0) +
      (this.storage.hasPartner ? this.partnerDiscount : 0)
    );
  }

  /**
   * Navigates to home with the url param order-page-redirect set to true to display the error modal.
   */
  protected override navigateToOrderErrorPage(): void {
    this.document.location.href = this.queryParamsService.buildRedirectWithPersistedParams(
      `${environment.siteUrls.tests}/?order-page-redirect=true`,
      this.document.location.href,
    );
  }

  /**
   * Navigates to home with the url param session-expired set to true to display the session expired modal.
   */
  protected navigateToSessionExpiredErrorPage(): void {
    this.document.location.href = `${environment.siteUrls.tests}/?session-expired=true`;
  }

  /**
   * Map HealthLabs cart response tests to project structure.
   *
   * @param {HealthLabsLoadCartResponse} response load cart response from HealthLabs API
   */
  protected mapHlCartResponseTests(response: HealthLabsLoadCartResponse): Test[] {
    return response.tests.map(
      (test): Test => ({
        slug: test.slug,
        id: parseInt(test.test_id),
        name: test.name,
        type: test.type,
        price: parseFloat(test.price) * 100,
        entry_id: parseInt(test.id),
      }),
    );
  }

  /**
   * Returns the subtotal based on the tests
   *
   * @param {Test[]} tests tests to calculate the subtotal
   */
  protected getCartSubtotal(tests: Test[]): number {
    return tests.reduce((sum, current) => sum + current.price, 0);
  }

  /**
   * Returns empty object since address is provided by patient later, not in order request.
   *
   * @param {Address} _addressFormValues the address form values containing customer address information
   *
   * @returns {Partial<OrderRequest>} an empty object since address is handled separately
   */
  protected override getOrderFullAddress(_addressFormValues: Address): Partial<OrderRequest> {
    return {};
  }

  /**
   * Combines storage coupons and discounts into array for order submission.
   *
   * @returns {AnalyteCareCouponDto[] | null} coupons/discounts if they exist, null otherwise
   */
  protected override getOrderCoupon(): AnalyteCareCouponDto[] | null {
    const coupons = [];
    const storageCoupon = this.storage.coupon;

    if (storageCoupon) {
      coupons.push(new AnalyteCareCouponDto(storageCoupon.coupon_name, storageCoupon.coupon_code, storageCoupon.value));
    }

    const storageDiscount = this.storage.discount;

    if (storageDiscount) {
      coupons.push(storageDiscount);
    }

    return coupons.length > 0 ? coupons : null;
  }

  /**
   * Maps stored tests to order format with string test_id and reference_id.
   *
   * @returns {{ test_id: string; reference_id: string }[]} An array of objects for each test in storage
   */
  protected override getOrderTests(): { test_id: string; reference_id: string }[] {
    return this.storage.tests.map((test) => ({ test_id: test.id.toString(), reference_id: test.entry_id.toString() }));
  }

  /**
   * Handles errors from the load cart request or response validation.
   *
   * If an error occurs and the status code is 401 Unauthorized, it navigates to the session expired error page.
   *
   * @param {any} error the error to handle
   *
   * @returns {Observable<never>} an empty observable
   */
  protected override handleLoadCartError(error: HttpErrorResponse): Observable<never> {
    if (error.status === HttpStatusCode.Unauthorized) {
      this.navigateToSessionExpiredErrorPage();

      return of();
    }

    return super.handleLoadCartError(error);
  }

  /**
   * Reloads the cart with the in-home collection option when an in-home collection booking is submitted.
   */
  protected override listenForBookingSubmission(): void {
    this.inHomeService.bookingSubmitted$.subscribe(() => this.loadCart([], { hasInHomeCollection: true }).subscribe());
  }

  /**
   * Adds guardian information from patient form to the order request.
   *
   * @param {OrderRequest} orderRequest the order request to modify
   */
  protected override addGuardianInfoToOrderRequest(orderRequest: OrderRequest): void {
    orderRequest.parseGuardianInfo(this.checkoutForm.value.patient);
  }

  /**
   * Get the tests to be loaded in the cart, using the provided tests or URL tests.
   *
   * @param {Test[]} tests List of tests to be loaded in the cart.
   */
  protected override getLoadCartTests(tests?: Test[]): string[] {
    return tests ? tests.map((test) => test.id.toString()) : this.testsFromUrl;
  }

  /**
   * Constructs a GET request to clear the cart from the Craft session via the
   * HealthLabs API.
   *
   * @returns an Observable of the HttpResponse, with a no content body.
   */
  private clearCartFromSession(): Observable<void> {
    return this.http.get<void>(`${environment.apiUrl}/clear`);
  }
}
